/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/BusinessEventRequirementsDisplay/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { CircularProgress } from "@mui/material";
import { ReactComponent as CheckmarkIcon } from "../../assets/icons/BusinessEvents/CheckmarkIcon.svg";
import { ReactComponent as WarningIcon } from "../../assets/icons/BusinessEvents/WarningIcon.svg";
import styles from "./index.module.css";
import { getValidateRequirements } from "../../logic/businessEvents";

import AppointmentRegistrationModal from "../AppointmentRegistration";

const BusinessEventRequirementsDisplay = ({
  open,
  participant,
  eventData,
  currentUser,
  autoSelectRequirementId,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const registrationDialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const [isRegistering, setIsRegistering] = useState(false);
  const [requirements, setRequirements] = useState([]);
  const [loadedRequirementsKey, setLoadedRequirementsKey] = useState(null);
  const [selectedRequirement, setSelectedRequirement] = useState(null);

  // Identifies the participant whose requirements should be on screen, so the
  // loading state is derived from the current props instead of trailing them by
  // one effect cycle.
  const requirementsKey =
    open && participant?.idEvento && participant?.idEmpresaCompradora
      ? `${participant.idEvento}:${participant.idEmpresaCompradora}`
      : null;
  const isLoadingRequirements =
    requirementsKey !== null && loadedRequirementsKey !== requirementsKey;

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    } else {
      // Dialog already matches the desired open state
    }
  }, [open]);

  useEffect(() => {
    const handleClose = () => {
      onCloseRef.current();
    };

    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    dialog.addEventListener("close", handleClose);

    return () => dialog.removeEventListener("close", handleClose);
  }, []);

  useEffect(() => {
    if (!requirementsKey) {
      setLoadedRequirementsKey(null);
      setRequirements((previous) => (previous.length === 0 ? previous : []));
      return undefined;
    }

    let isCurrentRequest = true;
    getValidateRequirements(
      participant.idEvento,
      participant.idEmpresaCompradora,
      (fetchedRequirements) => {
        if (isCurrentRequest) {
          setRequirements(
            Array.isArray(fetchedRequirements) ? fetchedRequirements : [],
          );
        }
      },
      navigate,
    ).finally(() => {
      if (isCurrentRequest) {
        setLoadedRequirementsKey(requirementsKey);
      }
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [
    requirementsKey,
    participant?.idEvento,
    participant?.idEmpresaCompradora,
    navigate,
  ]);

  // Auto-select and open the specific requirement if autoSelectRequirementId is provided
  useEffect(() => {
    if (
      open &&
      !isLoadingRequirements &&
      autoSelectRequirementId &&
      requirements &&
      requirements.length > 0
    ) {
      const matchingRequirement = requirements.find(
        (req) => req._id === autoSelectRequirementId,
      );

      if (matchingRequirement) {
        // Automatically open the requirement registration modal
        setSelectedRequirement(matchingRequirement);
        setIsRegistering(true);
      }
    }
  }, [open, isLoadingRequirements, autoSelectRequirementId, requirements]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    const handleCancel = (event) => {
      if (isRegistering) {
        event.preventDefault();
      }
    };

    dialog.addEventListener("cancel", handleCancel);
    return () => dialog.removeEventListener("cancel", handleCancel);
  }, [isRegistering]);

  const handleViewRequirement = (requirement) => {
    if (isRegistering) {
      return;
    }
    setSelectedRequirement(requirement);
    setIsRegistering(true);
  };

  const handleRequestClose = () => {
    if (isRegistering) {
      return;
    }
    onClose();
  };

  const isRequirementUnfulfilled = (requirement) => {
    if (Object.hasOwn(requirement, "missingRequiredFields")) {
      return Object.values(requirement.missingRequiredFields).some(
        (v) => v === true,
      );
    } else {
      return false;
    }
  };

  return (
    <>
      {open && <div className={styles.overlay}></div>}
      {selectedRequirement && (
        <AppointmentRegistrationModal
          dialogRef={registrationDialogRef}
          selectedRequirement={selectedRequirement}
          participant={participant}
          eventData={eventData}
          currentUser={currentUser}
          open={isRegistering}
          onClose={() => {
            setSelectedRequirement(null);
            setIsRegistering(false);
          }}
          onSubmit={() => {
            setSelectedRequirement(null);
            setIsRegistering(false);
            onClose(participant);
          }}
        />
      )}

      <dialog
        ref={dialogRef}
        className={styles.requirementsModal}
        aria-busy={isRegistering || isLoadingRequirements}
      >
        {isRegistering && (
          <div className={styles.submittingOverlay} aria-hidden />
        )}
        <div className={styles.closeButtonContainer}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleRequestClose}
            disabled={isRegistering}
          >
            &times;
          </button>
        </div>
        <div className={styles.modalHeader}>
          <span className={styles.modalTitle}>
            {t("components.eventRequirementsDisplayModal.title") +
              " - " +
              participant?.nombreComercial}
          </span>
          <span className={styles.modalSubtitle}>
            {t("components.eventRequirementsDisplayModal.description")}
          </span>
          <div className={styles.requirementLeyend}>
            <div className={styles.requirementStatusItem}>
              <CheckmarkIcon className={styles.statusIcon} />
              <span className={styles.statusText}>
                {t(
                  "components.eventRequirementsDisplayModal.fulfilledRequirements",
                )}
              </span>
            </div>
            <div className={styles.requirementStatusItem}>
              <WarningIcon className={styles.statusIcon} />
              <span className={styles.statusText}>
                {t(
                  "components.eventRequirementsDisplayModal.missingRequirements",
                )}
              </span>
            </div>
          </div>
        </div>

        <div
          className={styles.requirementList}
          inert={isRegistering || undefined}
        >
          {isLoadingRequirements && (
            <div className={styles.loadingContainer}>
              <CircularProgress size={28} sx={{ color: "#4eabe7" }} />
            </div>
          )}
          {!isLoadingRequirements && requirements.length === 0 && (
            <div className={styles.noResults}>
              <p>{t("components.eventRequirementsDisplayModal.noResults")}</p>
            </div>
          )}
          {!isLoadingRequirements &&
            requirements.length > 0 &&
            requirements.map((req, index) => (
              <div
                key={index}
                className={`${styles.requirementItem} ${
                  isRequirementUnfulfilled(req)
                    ? styles.unfulfilled
                    : styles.fulfilled
                }`}
              >
                <div className={styles.requirementText}>
                  {isRequirementUnfulfilled(req) ? (
                    <WarningIcon className={styles.statusIcon} />
                  ) : (
                    <CheckmarkIcon className={styles.statusIcon} />
                  )}
                  <span className={styles.requirementTitle}>{req.nombre}</span>
                </div>
                <button
                  type="button"
                  className={styles.viewButton}
                  onClick={() => handleViewRequirement(req)}
                  disabled={isRegistering}
                >
                  {t(
                    "components.eventRequirementsDisplayModal.viewRequirement",
                  )}
                </button>
              </div>
            ))}
        </div>
      </dialog>
    </>
  );
};

export default BusinessEventRequirementsDisplay;
